import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * Portal-free modal / drawer — Field Notes rebuild (epic #950 PR-8,
 * formerly the console-shared `createModal` factory instantiation).
 * Keeps the factory's focus-trap and escape/backdrop-close contract:
 * Escape closes, Tab cycles within the panel, first focusable element
 * receives focus on open.
 *
 * When the panel holds NO enabled focusable element (every control
 * `disabled`, e.g. a ConfirmDialog in its `loading` state) both the
 * open-focus and the Tab handler fall back to the panel itself — it is
 * `tabIndex={-1}` for exactly that reason. Without the fallback, Tab
 * would walk out to the background page, which carries neither `inert`
 * nor `aria-hidden`, and `aria-modal="true"` would be a lie.
 */
interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly titleId: string;
  /** Element id describing the dialog body, for `aria-describedby`. */
  readonly descriptionId?: string;
  readonly children: ReactNode;
  readonly closeOnBackdrop?: boolean;
  readonly testId?: string;
  readonly variant?: ModalVariant;
}

/**
 * `drawer` is the wide right-hand detail panel; `drawer-start` is the
 * narrow left-hand navigation sheet the header hamburger opens below
 * `lg`; `palette` is the top-anchored ⌘K surface. All four share the
 * focus trap and the escape/backdrop contract above — the variant only
 * chooses where the panel sits and how wide it gets.
 */
export type ModalVariant = 'center' | 'drawer' | 'drawer-start' | 'palette';

const BACKDROP_CLASSES: Record<ModalVariant, string> = {
  center: 'fixed inset-0 z-50 flex items-center justify-center bg-ink/25 px-4',
  drawer: 'fixed inset-0 z-50 flex items-stretch justify-end bg-ink/25',
  'drawer-start': 'fixed inset-0 z-50 flex items-stretch justify-start bg-ink/25',
  palette: 'fixed inset-0 z-50 flex items-start justify-center bg-ink/25 px-4 pt-[12vh]',
};

const PANEL_CLASSES: Record<ModalVariant, string> = {
  center: 'w-full max-w-lg rounded-xl border border-line bg-card text-ink',
  drawer: 'h-full w-full max-w-[920px] overflow-hidden border-l border-line bg-card text-ink',
  'drawer-start':
    'flex h-full w-72 max-w-[85vw] flex-col overflow-y-auto overscroll-contain border-r border-line bg-card text-ink',
  palette:
    'w-full max-w-xl overflow-hidden rounded-xl border border-line bg-raised text-ink shadow-raised',
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  isOpen,
  onClose,
  titleId,
  descriptionId,
  children,
  closeOnBackdrop = true,
  testId,
  variant = 'center',
}: Props): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const panel = panelRef.current;
      if (panel === null) {
        return;
      }
      const focusable = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) {
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      if (active === last || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const panel = panelRef.current;
    if (panel === null) {
      return;
    }
    const first = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    if (first !== null) {
      first.focus();
      return;
    }
    panel.focus();
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      role="presentation"
      data-testid={testId}
      className={BACKDROP_CLASSES[variant]}
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className={PANEL_CLASSES[variant]}
      >
        {children}
      </div>
    </div>
  );
}
